import clsx from 'clsx';
import { usePDPData, usePDPState } from '~/features/product-details/context';
import { Typography } from '~/shared/components';
import { useCountdown } from '~/shared/hooks/countdown';
import { useFrame } from '~/shared/utils';
import { TimeLeft } from '~/shared/utils/calculate-time-left';
import { formatTimeOfDay } from '~/shared/utils/date-utils';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductPrelaunchNotice.module.scss';

type Props = React.HTMLAttributes<HTMLParagraphElement>;

type StatusKey =
    | 'pdp.countdown.prelaunch.status.inDays'
    | 'pdp.countdown.prelaunch.status.tomorrow'
    | 'pdp.countdown.prelaunch.status.thisEvening'
    | 'pdp.countdown.prelaunch.status.lessThanOneHour'
    | 'pdp.countdown.prelaunch.status.inAFewMinutes'
    | 'pdp.countdown.prelaunch.status.getReady';

type StatusCopy = {
    key: StatusKey;
    values?: Record<string, string>;
};

const STATUS_FALLBACKS: Record<StatusKey, string> = {
    'pdp.countdown.prelaunch.status.inDays': 'Kan købes kl. {{time}} om {{days}} dage',
    'pdp.countdown.prelaunch.status.tomorrow': 'Kan købes i morgen kl. {{time}}',
    'pdp.countdown.prelaunch.status.thisEvening': 'Kan købes i aften kl. {{time}}',
    'pdp.countdown.prelaunch.status.lessThanOneHour': 'Kan købes om under 1 time',
    'pdp.countdown.prelaunch.status.inAFewMinutes': 'Kan købes om få minutter',
    'pdp.countdown.prelaunch.status.getReady': 'Gør dig klar!',
};

/**
 * Picks the copy for how far off the launch is. `launchTime` comes in already formatted: the
 * remaining duration cannot answer which hour it ends on.
 */
const getStatusCopy = (timeLeft: TimeLeft, launchTime: string): StatusCopy => {
    if (timeLeft.days >= 2) {
        return {
            key: 'pdp.countdown.prelaunch.status.inDays',
            values: { days: String(timeLeft.days), time: launchTime },
        };
    }

    if (timeLeft.days === 1) {
        return {
            key: 'pdp.countdown.prelaunch.status.tomorrow',
            values: { time: launchTime },
        };
    }

    if (timeLeft.hours >= 1) {
        return {
            key: 'pdp.countdown.prelaunch.status.thisEvening',
            values: { time: launchTime },
        };
    }

    if (timeLeft.minutes >= 10) {
        return { key: 'pdp.countdown.prelaunch.status.lessThanOneHour' };
    }

    if (timeLeft.minutes >= 5) {
        return { key: 'pdp.countdown.prelaunch.status.inAFewMinutes' };
    }

    return { key: 'pdp.countdown.prelaunch.status.getReady' };
};

const getStatusFallback = ({ key, values }: StatusCopy): string =>
    Object.entries(values ?? {}).reduce(
        (text, [name, value]) => text.split(`{{${name}}}`).join(value),
        STATUS_FALLBACKS[key],
    );

/**
 * Says when the product becomes buyable, in words rather than digits — the countdown above it
 * already carries the digits.
 */
export const ProductPrelaunchNotice: React.FC<Props> = ({ className, ...rest }) => {
    const { product } = usePDPData();
    const { isPrelaunchBlockingBuy } = usePDPState();

    if (!isPrelaunchBlockingBuy || !product.c_prelaunch) return null;

    return <PrelaunchNotice endDate={product.c_prelaunch} className={className} {...rest} />;
};

const PrelaunchNotice: React.FC<Props & { endDate: string }> = ({
    endDate,
    className,
    ...rest
}) => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { timeLeft, isExpired, hasMounted, endDate: launchDate } = useCountdown({ endDate });

    /*
     * The duration reads as all zeros until the countdown mounts, which the ladder above would
     * take for "any moment now". There is nothing to say before the timer is live.
     */
    if (!hasMounted || isExpired) return null;

    const copy = getStatusCopy(timeLeft, formatTimeOfDay(launchDate, frame.culture));

    return (
        <Typography
            className={clsx(styles.productPrelaunchNotice, className)}
            variant="paragraph-S"
            as="p"
            {...rest}
        >
            {translate(copy.key, copy.values) || getStatusFallback(copy)}
        </Typography>
    );
};
